// T134 — shared SEO/GEO primitives. Pure, no IO (app/lib rule).
//
// `SITE_ORIGIN` is the canonical production host. We deliberately emit a FIXED
// canonical origin (not the request host) so that canonical/hreflang/sitemap
// URLs always point at production even when a page is served from a Cloudflare
// preview deployment — the standard SEO posture (a preview must never claim to
// be the canonical copy under its own ephemeral host).
//
// Every host literal now lives in `app/config/hosts.ts` — including the ones the
// services used to declare for themselves. See that module for why the serving
// origin, the brand entity id and the link base are three constants, not one.
// Relative, not `~/`: T574's redirect policy (`app/config/host-redirect-policy.ts`) reads
// `INDEXABLE_PUBLIC_ROUTE_KEYS` from here and is imported by `functions/[[path]].ts`, which
// wrangler builds without the tsconfig path alias. Both targets are import-free modules.
import { SERVING_ORIGIN } from "../../config/hosts";
import {
  buildLocalizedPath,
  SUPPORTED_LANGS,
  type Lang,
  type RouteKey,
} from "../../config/route-config";

// T424 — the host literal itself lives in `app/config/hosts.ts`, the single owner.
// `SITE_ORIGIN` stays as the name the whole T134 GEO layer already imports; it is
// now an alias rather than a second declaration.
export const SITE_ORIGIN = SERVING_ORIGIN;

export const SITE_NAME = "TheFittClub";

/**
 * T898 — a page title as it appears in the tab and in search results:
 * `<page> | TheFittClub`. One owner for the suffix, so a route that composes its
 * title from translated copy cannot drop the brand (the legal documents did).
 */
export function brandedTitle(pageTitle: string): string {
  return `${pageTitle} | ${SITE_NAME}`;
}

/**
 * T735 — the social card: one image per language, and its real dimensions.
 *
 * Until 2026-09-13 this was `/og-default.png`, a 512×512 file byte-identical to
 * `public/icon-512.png` (md5 `d5f91dd395f9c1a7e860f797f5b1934d`). The social card
 * was not a badly cropped image — it was the app icon. Every platform renders a
 * link preview in landscape, so a square is cropped or letterboxed.
 *
 * The founder supplied the approved artwork at 1731×909 on 2026-09-13; these are
 * the 1200×630 exports of it, one per language because the claim is burnt into
 * the image (`El club de las que se eligen.` / `The club for women who choose
 * themselves.`). JPEG, not PNG: the same photograph is 1.4 MB as PNG and 120 KB
 * as JPEG at q88 4:4:4, and a link preview is decoded on someone else's phone.
 *
 * `width` / `height` live here rather than hardcoded at the tag so the numbers we
 * declare to Facebook and X have one source. `tests/unit/lib/og-image-asset.test.ts`
 * reads both files off disk and fails if any of it drifts.
 */
export const OG_IMAGE = {
  width: 1200,
  height: 630,
  paths: {
    es: "/thefittclub-og-default-es.jpg",
    en: "/thefittclub-og-default-en.jpg",
  },
} as const satisfies { width: number; height: number; paths: Record<Lang, string> };

/** The card for `lang`. */
export function ogImageFor(lang: Lang): string {
  return OG_IMAGE.paths[lang];
}

/** Spanish is the default surface; `ogImageFor` is what callers with a locale use. */
export const DEFAULT_OG_IMAGE = OG_IMAGE.paths.es;

export { SUPPORTED_LANGS };

/**
 * Public routes that should be indexed AND must carry full meta (canonical +
 * hreflang + Open Graph). This is the subset of `isPrivate === false` routes
 * that are content-bearing — transactional acquisition routes (signup,
 * checkout, reset-password, verify-email, onboarding, assessment…) are public
 * but excluded from the sitemap and from the lint's positive helper check.
 * Single source of truth consumed by the sitemap route and `lint-geo-routes`.
 */
export const INDEXABLE_PUBLIC_ROUTE_KEYS = [
  "home",
  // T735 — `ritualCore` left this list when its sales page was retired behind a
  // 301. A redirect in the sitemap is a crawl-budget leak and a contradiction:
  // the file claims the URL is a page, the server says it is not. T181 puts the
  // key back when it rebuilds the route as the real sales page.
  "metodo",
  "faq",
  "diastasis",
  "support",
  "legalDocument",
  "guias",
  "autoTest",
  "status",
] as const satisfies readonly RouteKey[];

/** Default sitemap change frequency per indexable route. */
export const ROUTE_CHANGEFREQ: Partial<Record<RouteKey, string>> = {
  home: "weekly",
  metodo: "monthly",
  faq: "monthly",
  diastasis: "monthly",
  support: "monthly",
  legalDocument: "monthly",
  guias: "weekly",
  autoTest: "monthly",
  status: "daily",
};

/** Absolute production URL for a route in `lang`, substituting dynamic params. */
export function absoluteUrl(
  routeKey: RouteKey,
  lang: Lang,
  params?: Record<string, string>,
): string {
  return SITE_ORIGIN + buildLocalizedPath(routeKey, lang, params);
}

/** Absolute production URL from a root-relative (or already absolute) path. */
export function absoluteFromPath(pathname: string): string {
  if (/^https?:\/\//.test(pathname)) return pathname;
  return SITE_ORIGIN + (pathname.startsWith("/") ? pathname : `/${pathname}`);
}

/** Derive the locale from an app pathname (`/en/ritual-core` → `en`); defaults to `es`. */
export function localeFromPath(pathname: string): Lang {
  const first = pathname.split("/").filter(Boolean)[0];
  return first === "en" ? "en" : "es";
}

/** Open Graph locale codes per supported language. */
export const OG_LOCALE: Record<Lang, string> = { es: "es_ES", en: "en_US" };
